"use client";

import { useEffect } from "react";

declare global {
  interface Window {
    silktideCookieBannerManager?: {
      updateCookieBannerConfig: (config: unknown) => void;
    };
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const SILKTIDE_CONFIG = {
  background: { showBackground: true },
  cookieIcon: { position: "bottomRight" },
  cookieTypes: [
    {
      id: "necessary",
      name: "Necessary",
      description:
        "<p>These cookies are necessary for the website to function properly and cannot be switched off. They help with things like logging in and setting your privacy preferences.</p>",
      required: true,
      onAccept: () => {},
    },
    {
      id: "analytics",
      name: "Analytics",
      description:
        "<p>These cookies help us improve the site by tracking which pages are most popular and how visitors move around the site.</p>",
      defaultValue: true,
      onAccept: () => {
        window.gtag?.("consent", "update", { analytics_storage: "granted" });
        window.dataLayer?.push({ event: "consent_accepted_analytics" });
      },
      onReject: () => {
        window.gtag?.("consent", "update", { analytics_storage: "denied" });
      },
    },
    {
      id: "advertising",
      name: "Advertising",
      description:
        "<p>These cookies provide extra features and personalization to improve your experience. They may be set by us or by partners whose services we use.</p>",
      onAccept: () => {
        window.gtag?.("consent", "update", {
          ad_storage: "granted",
          ad_user_data: "granted",
          ad_personalization: "granted",
        });
        window.dataLayer?.push({ event: "consent_accepted_advertising" });
      },
      onReject: () => {
        window.gtag?.("consent", "update", {
          ad_storage: "denied",
          ad_user_data: "denied",
          ad_personalization: "denied",
        });
      },
    },
  ],
  text: {
    banner: {
      description:
        '<p>We use cookies to enhance your experience, personalize content, and analyze traffic. <a href="/cookie-policy" target="_blank">Cookie Policy.</a></p>',
      acceptAllButtonText: "Accept all",
      acceptAllButtonAccessibleLabel: "Accept all cookies",
      rejectNonEssentialButtonText: "Reject non-essential",
      rejectNonEssentialButtonAccessibleLabel: "Reject non-essential",
      preferencesButtonText: "Preferences",
      preferencesButtonAccessibleLabel: "Toggle preferences",
    },
    preferences: {
      title: "Customize your cookie preferences",
      description:
        "<p>We respect your right to privacy. You can choose not to allow some types of cookies. Your cookie preferences will apply across our website.</p>",
    },
  },
};

export default function CookieBanner() {
  useEffect(() => {
    const apply = () => {
      window.silktideCookieBannerManager?.updateCookieBannerConfig(SILKTIDE_CONFIG);
    };

    if (window.silktideCookieBannerManager?.updateCookieBannerConfig) {
      apply();
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>(
      'script[src="/silktide/silktide-consent-manager.js"]',
    );
    if (existing) {
      existing.addEventListener("load", apply, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = "/silktide/silktide-consent-manager.js";
    script.onload = apply;
    document.head.appendChild(script);
  }, []);

  return null;
}
